import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Loader } from './Loader';
import LightboxImage from './LightboxImage';
import {
    isUserLoggedIn,
    incrementPaywallPageView,
    displayPaywallType
} from '../../tools/utilities';

const Lightbox = ({ open, index, issue, total, pages, path, paywall, closeLightbox, changeIndex }) => {
    const [loading, setLoading] = useState(true);
    const [shouldPaywall, setShouldPaywall] = useState(true);
    const [zoomEnabled, setZoomEnabled] = useState(false);
    const lightBoxMediaRef = useRef(null);
    const [startPan, setStartPan] = useState({x: 0, y: 0});
    const [isPan, setIsPan] = useState(false);

    const handlers = useSwipeable({
        onSwipedLeft: () => changeIndex(parseInt(index) + 1),
        onSwipedRight: () => changeIndex(parseInt(index) - 1),
        swipeDuration: 500,
        preventScrollOnSwipe: true,
        trackMouse: true,
    });

    const shouldDisplayPaywall = () => {
        setLoading(true);

        if (index && pages[parseInt(index - 1)].issue_content) {
            setShouldPaywall(displayPaywallType(
                pages[parseInt(index - 1)].paywall_type,
                pages[parseInt(index - 1)].paywall_entitlement
            ));
        } else {
            setShouldPaywall(false);
        }

        setLoading(false);
    };

    const pageUpdate = () => {
        if (index && pages[parseInt(index - 1)].issue_content) {
            incrementPaywallPageView();
        }

        shouldDisplayPaywall();
    };

    const toggleZoom = () => {
        setZoomEnabled(zoomEnabled ? false : true);
    }

    const panStart = (event) => {
        if (lightBoxMediaRef.current !== null) {
            event.preventDefault();
            setIsPan(true);
            setStartPan({
                x: lightBoxMediaRef.current.scrollLeft + event.clientX,
                y: lightBoxMediaRef.current.scrollTop + event.clientY
            });
        }
    };

    const panMove = (event) => {
        if (isPan && lightBoxMediaRef.current !== null) {
            lightBoxMediaRef.current.scrollTo(
                startPan.x - event.clientX,
                startPan.y - event.clientY
            );
        }
    };

    const panEnd = () => {
        setIsPan(false);
    };

    useEffect(() => {
        // If logged will need to wait for PelcroUserLoaded instead
        if (!isUserLoggedIn()) {
            shouldDisplayPaywall();
        }

        const close = e => {
            if (e.keyCode === 27 || e.key == 'Escape') {
                closeLightbox();
            }
        };
        window.addEventListener('keydown', close);
        return () => window.removeEventListener('keydown', close);
    }, []);

    useEffect(() => {
        pageUpdate();
    }, [index]);

    document.addEventListener('PelcroUserLoaded', () => {
        shouldDisplayPaywall();
    });

    document.addEventListener('PelcroUserUpdated', () => {
        shouldDisplayPaywall();
    });

    return (
        <>
            {open ? (
                <div className="lightbox" {...handlers}>
                    {!loading ? (
                        <span className="lightbox__counter">
                            {index} / {total}
                        </span>
                    ) : null}
                    <span className="lightbox__header">
                        {!shouldPaywall && <span className="lightbox__zoom" onClick={() => toggleZoom()}>
                            <div className={'zoom-icon ' + (zoomEnabled ? ' zoom-icon--active' : '')}>
                                <div class="zoom-icon__inner"></div>
                            </div>
                        </span>}
                        <span className="lightbox__close" onClick={() => closeLightbox()}>&times;</span>
                    </span>
                    <div
                        ref={lightBoxMediaRef}
                        className={'lightbox__media ' + (zoomEnabled ? ' lightbox__media--zoom-enabled' : '')}
                        onPointerDown={(event) => panStart(event)}
                        onPointerMove={(event) => panMove(event)}
                        onPointerUp={(event) => panEnd(event)}>
                        {loading ? (
                            <Loader />
                        ) : (
                            <>
                                {shouldPaywall ? (
                                    <div className="paywall-modal">
                                        {paywall.logo ? (
                                            <img className="paywall-modal__logo" src={paywall.logo} />
                                        ) : null}
                                        {paywall.title ? (
                                            <h2 className="paywall-modal__title">{paywall.title}</h2>
                                        ) : null}
                                        {paywall.sub_title ? (
                                            <h4 className="paywall-modal__sub-title">
                                                {paywall.sub_title}
                                            </h4>
                                        ) : null}
                                        <hr className="paywall-modal__divider" />
                                        {paywall.content ? (
                                            <div
                                                className="paywall-modal__body"
                                                dangerouslySetInnerHTML={{ __html: paywall.content }}
                                            />
                                        ) : null}
                                        {paywall.subscribe_link && paywall.subscribe_link.url ? (
                                            <a
                                                className="paywall-modal__cta"
                                                href={paywall.subscribe_link.url}>
                                                {paywall.subscribe_link.label ? paywall.subscribe_link.label : 'Subscribe'}
                                            </a>
                                        ) : 
                                            <button className="paywall-modal__cta pelcro-subscribe-button"
                                                data-product-id={paywall.pelcro_product_id} data-plan-id={paywall.pelcro_plan_id}>
                                                {paywall.subscribe_link.label ? paywall.subscribe_link.label : 'Subscribe'}
                                            </button>}
                                    </div>
                                ) : (
                                    <LightboxImage path={path} issue={issue} index={index} />
                                )}
                            </>
                        )}
                    </div>
                    {!loading ? (
                        <>
                            <span
                                className="lightbox__arrow lightbox__arrow--prev"
                                disabled={index == 1 ? true : false}
                                onClick={() => changeIndex(parseInt(index) - 1)}>
                                Prev
                            </span>
                            <span
                                className="lightbox__arrow lightbox__arrow--next"
                                disabled={index == total ? true : false}
                                onClick={() => changeIndex(parseInt(index) + 1)}>
                                Next
                            </span>
                            <div className="lightbox__footer">
                                {pages[parseInt(index) - 1].issue_content ? (
                                    <a className="lightbox__button" href={pages[parseInt(index) - 1].link}>
                                        View article
                                    </a>
                                ) : null}
                            </div>
                        </>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

export default Lightbox;
